'use client';

import React, { createContext, useContext, useMemo } from 'react';
import { DetectedDefinition } from '../schemas/DetectedDefinition';
import { useGetDefinitionsQuery } from '../state/definitionSlice';

/** 兩層 Map：外層 key = 第一層 definition.displayName; 內層 key = 各自 child.displayName */
export type DefinitionsMap = Map<string, Map<string, DetectedDefinition>>;

interface DefinitionsCtx {
    /** 全站唯一的 definitions 雙層 Map */
    definitionsMap: DefinitionsMap;
    /** 原始 array，如果偶爾需要 */
    rawDefinitions: DetectedDefinition[] | undefined;
}

const DefinitionsContext = createContext<DefinitionsCtx | undefined>(undefined);

/** Provider：集中 fetch 整理 Map，外層不需再管 */
export const DefinitionsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    // RTK Query：抓 definitions
    const { data: definitionData } = useGetDefinitionsQuery();

    /** 依目前 definitionData，建出雙層 Map；無資料時回傳空 Map */
    const definitionsMap: DefinitionsMap = useMemo(() => {
        const outer = new Map<string, Map<string, DetectedDefinition>>();
        if (!definitionData) return outer;

        definitionData.forEach((def) => {
            const subMap = new Map<string, DetectedDefinition>();

            // 先放自己
            subMap.set(def.displayName, def);

            // 再放 children
            def.childDefinitions.forEach((child) => {
                subMap.set(child.displayName, child);
            });

            outer.set(def.displayName, subMap);
        });

        return outer;
    }, [definitionData]);

    return (
        <DefinitionsContext.Provider value={{ definitionsMap, rawDefinitions: definitionData }}>
            {children}
        </DefinitionsContext.Provider>
    );
};

/** 在任何子元件裡呼叫；若 Provider 遺失會 throw，方便早期偵錯 */
export const useDefinitions = () => {
    const ctx = useContext(DefinitionsContext);
    if (!ctx) throw new Error('useDefinitions 必須包在 <DefinitionsProvider> 內使用');
    return ctx; // 回傳 { definitionsMap, rawDefinitions }
};
